import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DriveSweepModel } from '../../../../types';
import { UserKeys, startDriveMonitor } from '../../../api/users-api';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { Messages } from '../../../utils/message-constants';

export function useDriveSweepActions() {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [driveSweepToStart, setDriveSweepToStart] =
    useState<DriveSweepModel | null>(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate: startSweep, isPending: isStartingSweep } = useMutation({
    mutationFn: (data: DriveSweepModel) =>
      startDriveMonitor(data, changeLogMessage),
    onSuccess: (_data, variables: DriveSweepModel) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Started('Drive Sweep'),
      });
      queryClient.setQueryData(
        [UserKeys.DriveSweepModels],
        (oldData: DriveSweepModel[] = []) =>
          oldData.map((sweep) =>
            sweep.folderId === variables.folderId
              ? { ...sweep, isRunning: true, lastRun: Date.now() }
              : sweep
          )
      );
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      setDriveSweepToStart(null);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Starting('drive sweep'),
      });
    },
  });

  const handleStartDriveSweep = useCallback(
    (data: DriveSweepModel, changeLogRoles: string[]) => {
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          DriveRoles.CanStartDriveSweep.value
        )
      ) {
        setDriveSweepToStart(data);
        setOpenChangeLogDialog(true);
      } else {
        startSweep(data);
      }
    },
    [startSweep]
  );

  const handleRunWithChangeLog = useCallback(() => {
    if (driveSweepToStart) {
      startSweep(driveSweepToStart);
    }
  }, [driveSweepToStart, startSweep]);

  return {
    handleStartDriveSweep,
    handleRunWithChangeLog,
    isStartingSweep,
    openChangeLogDialog,
    setOpenChangeLogDialog,
    changeLogMessage,
    setChangeLogMessage,
    driveSweepToStart,
    setDriveSweepToStart,
  };
}
